// @react
import React from 'react'
// @types
import {
  Badge,
  Certification,
  GetConstantsQuery,
  GetConstantsQueryVariables,
  GetUserQuery,
  GetUserQueryVariables,
} from 'lib/graphql/graphqlTypes'
// @graphql
import { useQuery } from '@apollo/client'
import { GET_USER } from 'graphql/queries/user.query'
import { GET_CONSTANTS } from 'graphql/queries/constants.query'
// @libraries
import Helper from 'utils/Helper.class'
import strings from 'lib/constants/strings'
import { Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
// @components
import colors from '@bit/atd.web.colors/dist/colors'
import TextLink from '@bit/atd.web.text-link'
import Icon from '@bit/atd.web.icons'
import Spacer from '@bit/atd.web.spacer'
import Box from '@bit/atd.web.box'
import Button from '@bit/atd.web.button'
import SkeletonLoader from '@bit/atd.web.loaders.skeleton'
// @module components
import QueryError from 'modules/common/components/Errors/QueryError'

/**
 * 2.0.0.1 MyATD - Dashboard - Profile
 * https://www.figma.com/file/jCo0D2EYkVbuBX1TjR1enA/ATD-MyATD-Desktop-2020?node-id=3528%3A41884
 *
 * endpoint: user/profile
 * response: UserProfileDTO
 * response namespaces: User/Certification, User/Job, User/Certification
 *
 *
 * User	                userName
 * User	                ID
 * User	                firstName
 * User	                lastName
 * User	                profileImageUrl
 * User 	              staff
 * User/Job	            jobTitle
 * User/Job	            jobFunctionCode
 * User/Job	            jobIndustry
 * User/Certification	  type
 * User/Certification	  expiryDate
 * User/Certification	  masters
 */

const useStyles = makeStyles(() => ({
  subtitle: {
    color: '#313133',
    '& a': {
      display: 'inline',
      textDecoration: 'underline',
      wordBreak: 'break-word',
      whiteSpace: 'pre-line',
    },
  },
  certification: {
    '& div a span + span': {
      paddingLeft: '4px',
    },
    '& div + div': {
      fontSize: 0,
      marginLeft: '20px',
    },
    '& div + div small': {
      color: '#D70000',
      fontSize: '11px',
      lineHeight: '16px',
    },
  },
}))

const Profile = () => {
  /******************************************
   * HOOKS
   ******************************************/

  const classes = useStyles()

  /******************************************
   * GQL HOOKS
   ******************************************/

  const { loading: isLoading, error: isError, data } = useQuery<
    GetUserQuery,
    GetUserQueryVariables
  >(GET_USER, {
    fetchPolicy: 'cache-first',
  })

  const {
    // loading: isConstantsLoading,
    // error: isConstantsError,
    data: constantData,
  } = useQuery<GetConstantsQuery, GetConstantsQueryVariables>(GET_CONSTANTS, {
    fetchPolicy: 'cache-first',
  })

  /******************************************
   * RENDER
   ******************************************/

  if (isError || (!data?.user && !isLoading)) {
    return <QueryError />
  }

  const subtitle = constantData ? (
    <div className={classes.subtitle}>
      {/* in Industry */}
      {data?.user?.job.jobFunctionCode && (
        <>
          {/* Industry One */}
          <TextLink
            variant={'secondary'}
            href={`${Helper.constructSearchURL({
              refinementList: [
                {
                  refinement: 'roles',
                  value: Helper.constantsLabelFinder(
                    constantData.constants.jobRoleCodes,
                    data?.user?.job.jobFunctionCode || '',
                  ),
                },
              ],
            })}`}
          >
            <span>
              {Helper.constantsLabelFinder(
                constantData.constants.jobRoleCodes,
                data?.user?.job.jobFunctionCode || '',
              )}
            </span>
          </TextLink>
          {/* Industry Two */}
          {data?.user?.job.jobIndustry && (
            <>
              {' '}
              in{' '}
              <TextLink
                variant={'secondary'}
                href={`${Helper.constructSearchURL({
                  refinementList: [
                    {
                      refinement: 'industries',
                      value: Helper.constantsLabelFinder(
                        constantData.constants.industryCodes,
                        data?.user?.job.jobIndustry,
                      ),
                    },
                  ],
                })}`}
              >
                <span>
                  {Helper.constantsLabelFinder(
                    constantData.constants.industryCodes,
                    data?.user?.job.jobIndustry || '',
                  )}
                </span>
              </TextLink>
            </>
          )}
        </>
      )}
    </div>
  ) : (
    <></>
  )

  const renderBadge = (badge: Badge) => {
    const icon = 'masters'
    return (
      <div key={badge.id} className={classes.certification}>
        <div>
          <TextLink
            href={Helper.getPathFromRouteKey('MYATD_ACTIVITYHISTORY_LEARNING')}
            variant={'secondary'}
          >
            <span>
              <Icon size={16} glyph={icon} />
            </span>
            <span>{badge.title}</span>
          </TextLink>
        </div>
      </div>
    )
  }

  const renderCertificate = (certificate: Certification) => {
    const icon = 'certifications'

    return (
      <div className={classes.certification}>
        <div>
          <TextLink
            href={Helper.getPathFromRouteKey('MYATD_ACTIVITYHISTORY_LEARNING')}
            variant={'secondary'}
          >
            <span>
              <Icon size={16} glyph={icon} />
            </span>
            <span>{certificate.certificationCode}</span>
          </TextLink>
        </div>

        <div>
          {certificate.recertifyByDate ? (
            <Typography component={'small'}>
              RECERTIFY BY:{' '}
              <strong>
                {Helper.condensedDateFormatter(certificate.recertifyByDate)}
              </strong>
            </Typography>
          ) : null}
        </div>
      </div>
    )
  }

  const firstName = data?.user?.profile.firstName || ''
  const lastName = data?.user?.profile.lastName || ''
  const imageUrl = data?.user?.profile.profileImageUrl || ''

  //data
  return !isLoading ? (
    <Box
      variant="profile"
      topBorderHeight={64}
      header={{
        title: `${firstName} ${lastName}`,
        subtitle,
        actions: (
          <Button
            size={'small'}
            variant="tertiary"
            href={Helper.getPathFromRouteKey('PROFILE_PERSONALINFORMATION')}
          >
            <Icon glyph="edit" size={16} color={colors.ctaPrimary} />
            <Spacer size={8} />
            <span> Edit</span>
          </Button>
        ),
        avatar: {
          size: 80,
          alt: `${firstName} ${lastName}`,
          src: imageUrl,
          bgColor: Helper.randomColorChooser(),
          textAs: Helper.getAcronymFromSentence(`${firstName} ${lastName}`),
        },
      }}
      footer={{
        link: {
          url: Helper.getPathFromRouteKey('MYATD_ACTIVITYHISTORY_BOOKMARKS'),
          label: strings.DASHBOARD.MY_BOOKMARKS,
        },
      }}
    >
      {data?.user?.certification ? (
        data?.user?.certification?.map((cert) => renderCertificate(cert))
      ) : (
        <></>
      )}
      {data?.user?.badge ? (
        data?.user?.badge?.map((badge) => renderBadge(badge))
      ) : (
        <></>
      )}
    </Box>
  ) : (
    <Box
      variant="profile"
      topBorderHeight={64}
      header={{
        title: '',
        actions: <Spacer size={12} />,
      }}
    >
      <SkeletonLoader />
    </Box>
  )
}

export default Profile
